
import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';
import { useIsMobile } from '@/hooks/use-mobile';

interface TradingAccount {
  id: string | number;
  name?: string;
  number_account_trading?: string;
  isLive?: boolean;
  live?: boolean;
  balance?: number;
  balance_account?: number;
  currency?: string;
  api_connection_id?: string;
  isDisconnected?: boolean;
}

interface TradingAccountFieldProps {
  form: UseFormReturn<any>;
  isLoading: boolean;
  accounts: TradingAccount[];
  hasCspProfile?: boolean;
  disabled?: boolean;
}

export const TradingAccountField: React.FC<TradingAccountFieldProps> = ({
  form,
  isLoading,
  accounts,
  hasCspProfile = true,
  disabled = false
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <FormField
      control={form.control}
      name="trading_account_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('trading_account')}</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value}
            disabled={!hasCspProfile || isLoading || disabled}
          >
            <FormControl>
              <SelectTrigger className="h-10">
                <SelectValue placeholder={t('select_trading_account')} />
              </SelectTrigger>
            </FormControl>
            <SelectContent 
              position="popper"
              align="start"
              sideOffset={5}
              style={{ maxHeight: isMobile ? '40vh' : '50vh', overflowY: 'auto' }}
            >
              {isLoading ? (
                <SelectItem value="loading" disabled>{t('loading_accounts')}</SelectItem>
              ) : accounts.length > 0 ? (
                accounts.map(account => (
                  <SelectItem 
                    key={account.id.toString()} 
                    value={account.id.toString()} 
                    className={`py-${isMobile ? '2' : '3'} border-b border-gray-100 last:border-b-0`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium mr-4 truncate max-w-[120px]">
                        {account.name || account.number_account_trading}
                      </span>
                      <div className="flex items-center space-x-3">
                        <AccountTypeBadge isLive={account.isLive !== undefined ? account.isLive : account.live} />
                        {account.isDisconnected && (
                          <Badge variant="outline" className="border-amber-500 text-amber-500">
                            {t('disconnected')}
                          </Badge>
                        )}
                        <span className="font-medium">
                          ${((account.balance || account.balance_account || 0)).toLocaleString('en-US', { 
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2 
                          })} {account.currency || 'USD'}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="none" disabled>
                  {hasCspProfile 
                    ? t('no_available_trading_accounts')
                    : t('select_ctrader_profile_first')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
